/**
 * Страница подключения клиентских аккаунтов Telegram и MAX
 * Дата: 08.07.2026
 * Два независимых селектора tenant: Telegram Client и MAX Client
 */
import { useEffect, useState } from 'react';
import { logger } from '@/utils/logger';
import { MainLayout } from '@/components/layout/MainLayout';
import { SubsectionSwitch } from '@/components/ui/SubsectionSwitch';
import { LinearScreen } from '@/features/linear-canvas/LinearScreen';
import { useSubsectionParam } from '@/hooks/useSubsectionParam';
import { Card, CardHeader, CardTitle, CardContent, Button, TenantSelector } from '@/components/ui';
import { TelegramClientStatus } from '@/components/TelegramClientStatus';
import { TelegramQRAuth } from '@/components/TelegramQRAuth';
import { MaxClientStatus } from '@/components/MaxClientStatus';
import { MaxQRAuth } from '@/components/MaxQRAuth';
import { MaxPhoneAuth } from '@/components/MaxPhoneAuth';
import { checkAuthStatus as checkTelegramAuthStatus, type AuthStatus as TelegramAuthStatus } from '@/api/telegramClient';
import { checkAuthStatus as checkMaxAuthStatus, type AuthStatus as MaxAuthStatus } from '@/api/maxClient';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/hooks/useToast';
import { Building2, Smartphone } from 'lucide-react';

export function MessengerClientsSetupPage() {
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = !user?.tenant_id;
  const defaultTenantId = user?.tenant_id || '';

  const [telegramTenantId, setTelegramTenantId] = useState(defaultTenantId);
  const [maxTenantId, setMaxTenantId] = useState(defaultTenantId);

  const [showTelegramQR, setShowTelegramQR] = useState(false);
  const [showMaxQR, setShowMaxQR] = useState(false);
  const [showMaxPhone, setShowMaxPhone] = useState(false);

  const { showWarning } = useToast();

  useEffect(() => {
    logger.info('page_view', { page: 'messenger-clients' });
  }, []);

  useEffect(() => {
    if (!isSuperAdmin && defaultTenantId) {
      setTelegramTenantId(defaultTenantId);
      setMaxTenantId(defaultTenantId);
    }
  }, [isSuperAdmin, defaultTenantId]);

  const handleTelegramStartAuth = async () => {
    if (!telegramTenantId) {
      showWarning('Выберите клинику для Telegram Client');
      return;
    }
    try {
      const status = await checkTelegramAuthStatus(telegramTenantId);
      if (status.status === 'connected') {
        showWarning('Telegram аккаунт уже подключен');
        return;
      }
    } catch {
      // continue to QR
    }
    setShowTelegramQR(true);
  };

  const handleMaxStartAuth = async () => {
    if (!maxTenantId) {
      showWarning('Выберите клинику для MAX Client');
      return;
    }
    try {
      const status = await checkMaxAuthStatus(maxTenantId);
      if (status.status === 'connected') {
        showWarning('MAX аккаунт уже подключен');
        return;
      }
    } catch {
      // continue to QR
    }
    setShowMaxPhone(false);
    setShowMaxQR(true);
  };

  const handleMaxStartPhoneAuth = async () => {
    if (!maxTenantId) {
      showWarning('Выберите клинику для MAX Client');
      return;
    }
    try {
      const status = await checkMaxAuthStatus(maxTenantId);
      if (status.status === 'connected') {
        showWarning('MAX аккаунт уже подключен');
        return;
      }
    } catch {
      // continue to SMS
    }
    setShowMaxQR(false);
    setShowMaxPhone(true);
  };

  const handleTelegramAuthSuccess = (_status: TelegramAuthStatus) => {
    setShowTelegramQR(false);
    window.location.reload();
  };

  const [channel, setChannel] = useSubsectionParam('section', ['telegram', 'max'] as const, 'telegram');

  const handleMaxAuthSuccess = (_status: MaxAuthStatus) => {
    setShowMaxQR(false);
    setShowMaxPhone(false);
    window.location.reload();
  };

  return (
    <MainLayout headerTitle="Клиенты мессенджеров" disableMainScroll>
      <LinearScreen
        eyebrow="Каналы"
        title="Клиенты мессенджеров"
        lead="Живой аккаунт клиники, не бот. У Telegram и MAX свои клиники, начатый вход не сбрасывается при переключении."
        toolbar={
          <SubsectionSwitch
            testId="messenger-clients-section"
            value={channel}
            onChange={(id) => setChannel(id as 'telegram' | 'max')}
            items={[
              { id: 'telegram', label: 'Telegram Client' },
              { id: 'max', label: 'MAX Client' },
            ]}
          />
        }
      >
      <div className="mx-auto max-w-3xl">
          <div className={channel === 'telegram' ? 'space-y-4' : 'hidden space-y-4'}>
            {isSuperAdmin && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Building2 className="w-4 h-4" />
                    Клиника — Telegram Client
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TenantSelector value={telegramTenantId} onChange={setTelegramTenantId} />
                  {!telegramTenantId && (
                    <p className="text-sm text-amber-600 mt-2">
                      Выберите клинику для настройки Telegram Client
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {!telegramTenantId ? (
              <Card>
                <CardContent className="py-8 text-center text-neutral-500">
                  <Smartphone className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Выберите клинику для Telegram Client</p>
                </CardContent>
              </Card>
            ) : showTelegramQR ? (
              <TelegramQRAuth
                tenantId={telegramTenantId}
                onSuccess={handleTelegramAuthSuccess}
                onCancel={() => setShowTelegramQR(false)}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Telegram Client (MTProto)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <TelegramClientStatus tenantId={telegramTenantId} />
                  <Button onClick={handleTelegramStartAuth} variant="primary" className="w-full">
                    📱 Подключить Telegram через QR-код
                  </Button>
                  <p className="text-xs text-neutral-500">
                    Авторизация по номеру телефона и 2FA:{' '}
                    <a href="/telegram-client/advanced" className="text-primary-600 hover:underline">
                      расширенные настройки Telegram
                    </a>
                    .
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className={channel === 'max' ? 'space-y-4' : 'hidden space-y-4'}>
            {isSuperAdmin && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Building2 className="w-4 h-4" />
                    Клиника — MAX Client
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <TenantSelector value={maxTenantId} onChange={setMaxTenantId} />
                  {!maxTenantId && (
                    <p className="text-sm text-amber-600 mt-2">
                      Выберите клинику для настройки MAX Client
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {!maxTenantId ? (
              <Card>
                <CardContent className="py-8 text-center text-neutral-500">
                  <Smartphone className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Выберите клинику для MAX Client</p>
                </CardContent>
              </Card>
            ) : showMaxQR ? (
              <MaxQRAuth
                tenantId={maxTenantId}
                onSuccess={handleMaxAuthSuccess}
                onCancel={() => setShowMaxQR(false)}
              />
            ) : showMaxPhone ? (
              <MaxPhoneAuth
                tenantId={maxTenantId}
                onSuccess={handleMaxAuthSuccess}
                onCancel={() => setShowMaxPhone(false)}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>MAX Client (go-max-client)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <MaxClientStatus tenantId={maxTenantId} />
                  <Button onClick={handleMaxStartPhoneAuth} variant="primary" className="w-full">
                    📱 Подключить MAX по SMS-коду
                  </Button>
                  <Button onClick={handleMaxStartAuth} variant="secondary" className="w-full">
                    Подключить MAX через QR-код
                  </Button>
                  <p className="text-xs text-neutral-500">
                    SMS-вход работает как в web.max.ru: введите номер, получите код, подтвердите его.
                    QR-альтернатива: Профиль → Устройства → Войти по QR-коду.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
      </div>
      </LinearScreen>
    </MainLayout>
  );
}
